/**
 * The propose_relationship behavior in the browser — a run over a scope.
 *
 * SPEC-custom-agent-template.md §4: one runner, two hosts. This is the browser
 * host (Run now over a scope); the server host is
 * supabase/functions/ambient/lib/relationshipServer.ts (events, schedules,
 * tasks). Both build the prompt, parse the reply and apply the behavior's
 * switches with the shared core (supabase/functions/_shared/relationshipCore.ts),
 * and both write through propose_item_relationship — the same row either way.
 *
 * Every run: the agent must be on and the plan must include AI; each model
 * call counts against the agent's monthly cap; nothing is written the
 * definition does not declare; every proposal writes a decision record.
 */

import { getSupabaseClient } from './supabaseClient';
import { getAgentSettings, type AgentSettings } from './agentSettingsService';
import { NEXUS, NEXUS_AGENT_TYPE, agentReportHref, assertAgentMayProduce } from './ai/agentDefinitions';
import {
  applyRelationshipConfig,
  relationshipKey,
  resolveRelationshipConfigFrom,
  type RelationshipBehaviorConfig,
  type RelationshipResult,
} from '../../supabase/functions/_shared/relationshipCore.ts';
import { runNexusBatchAgent, NEXUS_BATCH_SIZE } from './llmNexusBatchService';
import { createNexusProposal } from './nexusAgentService';
import { countAgentModelCall, enqueueAgentDelivery } from './agentRuntimeService';
import { isWorkspaceEntitledToAi } from './ai/aiAssistSettings';
import { fetchItemsForScope } from './agentScopeItems';
import type { BatchStats, RunScope } from './agentRunService';
import { currentAgentRunId } from './agentRunService';

export interface RelationshipAgent {
  agentType: string;
  name: string;
}

export const NEXUS_IDENTITY: RelationshipAgent = { agentType: NEXUS_AGENT_TYPE, name: NEXUS.name };

export const RELATIONSHIP_STACK_VERSION = 'propose_relationship/2';

export function resolveRelationshipConfig(settings: AgentSettings | null): RelationshipBehaviorConfig {
  return resolveRelationshipConfigFrom(settings?.behaviorConfig);
}

const CONFIDENCE_SCORE = { low: 0.35, medium: 0.6, high: 0.85 } as const;

async function record(agent: RelationshipAgent, workspaceId: string, r: {
  itemId: string; outputKind: 'proposal:relationship' | 'error'; rationale: string; outputId?: string | null;
  proposedValue?: unknown; confidence?: number | null; runId?: string | null; runByUserId?: string | null;
}): Promise<void> {
  const { error } = await getSupabaseClient().rpc('record_agent_decision', {
    p_workspace_id: workspaceId,
    p_agent_type: agent.agentType,
    p_object_id: r.itemId,
    p_output_kind: r.outputKind,
    p_rationale: r.rationale,
    p_object_type: 'item',
    p_field: 'relationship',
    p_output_id: r.outputId ?? null,
    p_proposed_value: r.proposedValue ?? null,
    p_confidence: r.confidence ?? null,
    p_evidence: [],
    p_stack_version: RELATIONSHIP_STACK_VERSION,
    p_model: null,
    p_run_id: r.runId ?? null,
    p_sponsor_user_id: null,
    p_run_by_user_id: r.runByUserId ?? null,
    p_task_id: null,
  });
  if (error) console.warn('[relationships] decision record not written:', error.message);
}


/**
 * Run over a scope (matches AgentBatchRunner). Stats: processed = items sent
 * to the model; proposed = new relationship proposals; skipped = proposals
 * already open; errors = failed calls or saves. Progress is counted in batches.
 */
export async function runRelationshipBatch(
  workspaceId: string,
  scope: RunScope,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
  runByUserId?: string | null,
  agent: RelationshipAgent = NEXUS_IDENTITY,
): Promise<BatchStats> {
  const stats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };
  if (!workspaceId || !isWorkspaceEntitledToAi(workspaceId)) return stats;
  const { data: settings } = await getAgentSettings(workspaceId, agent.agentType);
  if (!settings?.enabled) return stats;
  const config = resolveRelationshipConfig(settings);
  const limits = (settings.limits ?? {}) as { perRunObjectCap?: number; failurePosture?: string; timeoutMs?: number };
  const cap = Number(limits.perRunObjectCap ?? NEXUS.limits.perRunObjectCap);
  const { items, error } = await fetchItemsForScope(workspaceId, scope, { agentType: agent.agentType, exitStatuses: ['active'], limit: cap });
  if (error) { stats.errors += 1; return stats; }
  stats.itemsRead = items.length;
  if (items.length < 2) return stats;

  const runId = await currentAgentRunId(workspaceId, agent.agentType);
  const failStop = limits.failurePosture === 'fail_run';
  const docs = ({ reference_docs: true, ...(settings.evidenceSources ?? {}) }).reference_docs ? settings.referenceDocs ?? [] : [];
  const seen = new Set<string>();
  const proposedItems = new Set<string>();
  // Progress is counted in items, as every other runner counts it.
  const total = items.length;
  onProgress(0, total);

  for (let offset = 0; offset < items.length; offset += NEXUS_BATCH_SIZE) {
    const done = Math.min(offset + NEXUS_BATCH_SIZE, total);
    if (signal.aborted) break;
    const batch = items.slice(offset, offset + NEXUS_BATCH_SIZE);
    if (batch.length < 2) { onProgress(done, total); continue; }

    const meter = await countAgentModelCall(workspaceId, agent.agentType);
    if (!meter.allowed) {
      await record(agent, workspaceId, { itemId: batch[0].id, outputKind: 'error', rationale: meter.error ?? `${agent.name} has used its model calls for this month.`, runId, runByUserId });
      stats.errors += 1;
      break;
    }
    let results: RelationshipResult[];
    try {
      results = await runNexusBatchAgent({
        items: batch.map((i) => ({ id: i.id, title: i.title, description: i.description, category: i.category ?? null })),
        model: settings.model,
        systemPrompt: settings.systemPrompt ?? null,
        modelParams: settings.modelParams ?? null,
        referenceDocs: docs,
        timeoutMs: limits.timeoutMs,
      });
    } catch (err) {
      await record(agent, workspaceId, { itemId: batch[0].id, outputKind: 'error', rationale: err instanceof Error ? err.message : 'The model call failed.', runId, runByUserId });
      stats.errors += 1;
      stats.processed += batch.length;
      onProgress(done, total);
      if (failStop) break;
      continue;
    }
    stats.processed += batch.length;

    for (const r of applyRelationshipConfig(results, config)) {
      const key = relationshipKey(r);
      if (seen.has(key)) continue;
      seen.add(key);
      assertAgentMayProduce(NEXUS, 'proposal:relationship');
      const { data, existing, error: saveError } = await createNexusProposal({
        workspaceId,
        sourceItemId: r.source_item_id,
        targetItemId: r.target_item_id,
        relationshipType: r.relationship_type,
        relationshipSubtype: r.relationship_subtype,
        directionality: r.directionality,
        sourceItemRole: r.source_item_role,
        rationale: r.rationale,
        confidence: r.confidence,
        proposedByAgent: agent.agentType,
      });
      if (saveError || !data) { stats.errors += 1; continue; }
      if (existing) { stats.skipped += 1; continue; }
      await record(agent, workspaceId, {
        itemId: r.source_item_id, outputKind: 'proposal:relationship', outputId: data.id, rationale: r.rationale,
        proposedValue: { type: r.relationship_type, subtype: r.relationship_subtype ?? null, source: r.source_item_id, target: r.target_item_id },
        confidence: CONFIDENCE_SCORE[r.confidence], runId, runByUserId,
      });
      stats.proposed += 1;
      proposedItems.add(r.source_item_id);
      proposedItems.add(r.target_item_id);
    }
    onProgress(done, total);
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }

  if (stats.proposed) {
    await enqueueAgentDelivery({
      workspaceId,
      agentType: agent.agentType,
      title: `${agent.name} proposed ${stats.proposed} relationship${stats.proposed === 1 ? '' : 's'}`,
      body: `${stats.proposed} relationship${stats.proposed === 1 ? '' : 's'} are waiting for review.`,
      link: agentReportHref(agent.agentType),
      itemIds: [...proposedItems],
      runId,
    });
  }
  return stats;
}
